import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Droplet, 
  Map, 
  Users, 
  Calendar, 
  AlertTriangle, 
  Bell, 
  FileText, 
  Shield,
  Plus,
  Wrench,
  Heart,
  CloudRain,
  User,
  Settings,
  LogOut,
  CheckCheck,
  X,
  Loader,
  Activity,
  PanelLeft
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getUserProfile } from '../lib/waterAuth';
import { Card, Button, Badge } from '../components/ui';

// Global statistics (mock data)
const globalStats = {
  totalWaterSources: 1248,
  sourcesAdded: 32,
  activeMembers: 856,
  upcomingEvents: 15
};

// Sample water sources data
const waterSourcesData = [
  {
    id: 1,
    name: "Sacred Blue River",
    location: {
      city: "New York",
      region: "New York",
      country: "USA"
    },
    status: "abundant",
    approved: true
  },
  {
    id: 2,
    name: "Crystal Spring",
    location: {
      city: "Los Angeles",
      region: "California",
      country: "USA"
    },
    status: "reduced",
    approved: true
  },
  {
    id: 3,
    name: "Mountain Creek",
    location: {
      city: "Boulder",
      region: "Colorado",
      country: "USA"
    },
    status: "normal",
    approved: false
  }
];

// Sample upcoming events
const upcomingEventsData = [
  {
    id: 1,
    title: "Global Water Blessing Ceremony",
    date: new Date(new Date().setDate(new Date().getDate() + 14)),
    type: "ceremony",
    online: true
  },
  {
    id: 2,
    title: "River Clean-up Day",
    date: new Date(new Date().setDate(new Date().getDate() + 7)),
    type: "cleanup",
    online: false
  }
];

// Sample wisdom resources
const wisdomResourcesData = [
  {
    id: 1,
    title: "Water is Life: Elder Teachings",
    type: "video",
    approved: true
  },
  {
    id: 2,
    title: "Water Blessing Protocol",
    type: "text",
    approved: true
  }
];

const WaterUnityDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [waterSources, setWaterSources] = useState(waterSourcesData);
  const [upcomingEvents, setUpcomingEvents] = useState(upcomingEventsData);
  const [wisdomResources, setWisdomResources] = useState(wisdomResourcesData);
  const [unreadNotifications, setUnreadNotifications] = useState(3);

  useEffect(() => {
    const checkAuth = async () => {
      const { data } = await supabase.auth.getSession();
      
      if (!data.session) {
        navigate('/login');
        return;
      }
      
      try {
        setLoading(true);
        const profileResult = await getUserProfile();
        
        if (profileResult.success) {
          setUserProfile(profileResult.profile);
        } else {
          // User is logged in but doesn't have a Water Unity profile
          setUserProfile(null);
        }
      } catch (error) {
        console.error("Error fetching user profile:", error);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, [navigate]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const renderTabContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <Loader className="h-8 w-8 text-blue-600 animate-spin" />
            <p className="text-gray-600">Loading your Water Unity dashboard...</p>
          </div>
        </div>
      );
    }

    if (!userProfile && activeTab !== 'join') {
      return (
        <Card>
          <div className="text-center py-8">
            <div className="bg-blue-100 rounded-full p-4 inline-flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-xl font-bold mb-2">Complete Your Profile</h3>
            <p className="text-gray-600 mb-6">
              You need to create a Water Unity profile to access all features.
            </p>
            <Button
              variant="primary"
              className="bg-blue-600 hover:bg-blue-700"
              onClick={() => setActiveTab('join')}
            >
              <span className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Create Your Profile
              </span>
            </Button>
          </div>
        </Card>
      );
    }

    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="text-center p-6">
                <h3 className="text-gray-600 text-sm mb-1">Water Sources</h3>
                <p className="text-3xl font-bold text-blue-600">{globalStats.totalWaterSources}</p>
                <span className="text-xs text-green-600">+{globalStats.sourcesAdded} this month</span>
              </Card>
              
              <Card className="text-center p-6">
                <h3 className="text-gray-600 text-sm mb-1">Active Members</h3>
                <p className="text-3xl font-bold text-blue-600">{globalStats.activeMembers}</p>
                <span className="text-xs text-green-600">From 42 countries</span>
              </Card>
              
              <Card className="text-center p-6">
                <h3 className="text-gray-600 text-sm mb-1">Upcoming Events</h3>
                <p className="text-3xl font-bold text-blue-600">{globalStats.upcomingEvents}</p>
                <span className="text-xs text-green-600">5 ceremonies</span>
              </Card>
              
              <Card className="text-center p-6">
                <h3 className="text-gray-600 text-sm mb-1">Your Submissions</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {userProfile ? '3' : '0'}
                </p>
                <span className="text-xs text-green-600">
                  {userProfile ? '2 approved' : 'Create profile first'}
                </span>
              </Card>
            </div>
            
            {/* Recent Water Sources */}
            <Card>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold flex items-center gap-2">
                  <Droplet className="h-5 w-5 text-blue-600" />
                  <span>Recent Water Sources</span>
                </h3>
                <Link 
                  to="/water-unity/sources" 
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  View All
                </Link>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {waterSources.map((source) => (
                      <tr key={source.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {source.name}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                          {`${source.location.city}, ${source.location.country}`}
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Badge 
                            variant={
                              source.status === 'abundant' ? 'blue' : 
                              source.status === 'normal' ? 'green' : 
                              source.status === 'reduced' ? 'amber' : 
                              source.status === 'critical' ? 'red' : 
                              source.status === 'restored' ? 'purple' : 
                              'teal'
                            } 
                            className="capitalize"
                          >
                            {source.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          {source.approved ? (
                            <Badge variant="green" icon={<CheckCheck className="h-3 w-3" />}>
                              Approved
                            </Badge>
                          ) : (
                            <Badge variant="amber" icon={<Activity className="h-3 w-3" />}>
                              Pending Review
                            </Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            
            {/* Upcoming Events and Resources */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Upcoming Events */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                    <span>Upcoming Events</span>
                  </h3>
                  <Link 
                    to="/water-unity/events" 
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View All
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {upcomingEvents.map((event) => (
                    <div key={event.id} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Calendar className="h-5 w-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{event.title}</h4>
                        <p className="text-sm text-gray-600">
                          {event.date.toLocaleDateString('en-US', { 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                          {' • '}
                          {event.online ? 'Online' : 'In Person'}
                        </p>
                        <Badge 
                          variant={
                            event.type === 'ceremony' ? 'purple' : 
                            event.type === 'cleanup' ? 'green' : 
                            'blue'
                          } 
                          className="mt-2 capitalize text-xs"
                        >
                          {event.type}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
              
              {/* Recent Resources */}
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    <span>Recent Resources</span>
                  </h3>
                  <Link 
                    to="/water-unity/resources" 
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    View All
                  </Link>
                </div>
                
                <div className="space-y-4">
                  {wisdomResources.map((resource) => (
                    <div key={resource.id} className="flex items-start gap-3 p-3 hover:bg-gray-50 rounded-lg">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        {resource.type === 'video' ? (
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                            <polygon points="5 3 19 12 5 21 5 3"></polygon>
                          </svg>
                        ) : (
                          <FileText className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium">{resource.title}</h4>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge 
                            variant={resource.type === 'video' ? 'red' : 'blue'} 
                            className="capitalize text-xs"
                          >
                            {resource.type}
                          </Badge>
                          {resource.approved && (
                            <Badge variant="green" className="text-xs">
                              Approved
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        );
      
      case 'join':
        return (
          <Card>
            <h2 className="text-xl font-bold mb-6">Create Your Water Unity Profile</h2>
            <form className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tribal/Cultural Lineage (if applicable)
                  </label>
                  <input
                    type="text"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Role *
                </label>
                <select
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select your role...</option>
                  <option value="Elder">Elder</option>
                  <option value="Guardian">Guardian</option>
                  <option value="Youth">Youth</option>
                  <option value="Artist">Artist</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City/Community *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your Water Story (Optional)
                </label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Share your personal connection to water, your journey, or your vision..."
                ></textarea>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Water Ritual History (Optional)
                </label>
                <textarea
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Share information about water ceremonies or rituals you practice or participate in..."
                ></textarea>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <div className="flex items-start gap-2">
                  <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800 font-medium">Privacy & Cultural Respect</p>
                    <p className="text-sm text-blue-600 mt-1">
                      Share only what you're comfortable with. For cultural practices, share only what is appropriate for public knowledge. Sensitive or sacred information should remain protected.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end gap-4">
                <Button
                  variant="secondary"
                  onClick={() => setActiveTab('overview')}
                >
                  Cancel
                </Button>
                
                <Button
                  variant="primary"
                  className="bg-blue-600 hover:bg-blue-700"
                  type="submit"
                >
                  Create Profile
                </Button>
              </div>
            </form>
          </Card>
        );
      
      case 'sources':
        return (
          <Card>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">Water Sources</h2>
              <Button
                variant="primary"
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Link to="/water-unity/submit-source" className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Add Water Source
                </Link>
              </Button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {waterSources.map((source) => (
                    <tr key={source.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {source.name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {`${source.location.city}, ${source.location.country}`}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <Badge 
                          variant={
                            source.status === 'abundant' ? 'blue' : 
                            source.status === 'normal' ? 'green' : 
                            source.status === 'reduced' ? 'amber' : 
                            source.status === 'critical' ? 'red' : 
                            source.status === 'restored' ? 'purple' : 
                            'teal'
                          } 
                          className="capitalize"
                        >
                          {source.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        {source.approved ? (
                          <Badge variant="green" icon={<CheckCheck className="h-3 w-3" />}>
                            Approved
                          </Badge>
                        ) : (
                          <Badge variant="amber" icon={<Activity className="h-3 w-3" />}>
                            Pending Review
                          </Badge>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button className="text-blue-600 hover:text-blue-800 mr-3">
                          View
                        </button>
                        {!source.approved && (
                          <button className="text-red-600 hover:text-red-800">
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        );
      
      case 'events':
      case 'resources':
      case 'map':
      case 'members':
      case 'settings':
        return (
          <Card className="text-center py-12">
            <div className="flex flex-col items-center justify-center gap-4">
              <Wrench className="h-12 w-12 text-blue-600" />
              <h3 className="text-xl font-bold">Under Construction</h3>
              <p className="text-gray-600 max-w-md">
                This section of the Water Unity dashboard is currently under development. Please check back soon!
              </p>
            </div>
          </Card>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-blue-50 pt-20">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-6 py-8">
          {/* Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-md p-6 sticky top-24">
              <div className="mb-6 text-center">
                <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Droplet className="h-8 w-8 text-blue-600" />
                </div>
                <h2 className="font-bold text-lg">Water Unity</h2>
                <p className="text-sm text-gray-600">Dashboard</p>
              </div>
              
              <nav className="space-y-1">
                <button 
                  onClick={() => setActiveTab('overview')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === 'overview' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Activity className="h-5 w-5" />
                  <span>Overview</span>
                </button>
                
                <button 
                  onClick={() => setActiveTab('sources')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === 'sources' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Droplet className="h-5 w-5" />
                  <span>Water Sources</span>
                </button>
                
                <button 
                  onClick={() => setActiveTab('events')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === 'events' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Calendar className="h-5 w-5" />
                  <span>Events</span>
                </button>
                
                <button 
                  onClick={() => setActiveTab('resources')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === 'resources' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <FileText className="h-5 w-5" />
                  <span>Resources</span>
                </button>
                
                <button 
                  onClick={() => setActiveTab('map')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === 'map' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Map className="h-5 w-5" />
                  <span>Water Map</span>
                </button>
                
                <button 
                  onClick={() => setActiveTab('members')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === 'members' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Users className="h-5 w-5" />
                  <span>Members</span>
                </button>
                
                {!userProfile && (
                  <button 
                    onClick={() => setActiveTab('join')}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                      activeTab === 'join' 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                    }`}
                  >
                    <User className="h-5 w-5" />
                    <span>Create Profile</span>
                  </button>
                )}
                
                <button 
                  onClick={() => setActiveTab('settings')}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    activeTab === 'settings' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Settings className="h-5 w-5" />
                  <span>Settings</span>
                </button>
                
                <hr className="my-2 border-gray-200" />
                
                <button 
                  onClick={handleSignOut}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Sign Out</span>
                </button>
              </nav>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="flex-1">
            {/* Header */}
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold">Water Unity Dashboard</h1>
                  <p className="text-gray-600">
                    {new Date().toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <button className="text-gray-600 hover:text-gray-800 relative">
                      <Bell className="h-6 w-6" />
                      {unreadNotifications > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                          {unreadNotifications}
                        </span>
                      )}
                    </button>
                  </div>
                  
                  <div className="hidden md:flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="font-medium">
                        {userProfile ? userProfile.name : 'Guest User'}
                      </div>
                      <div className="text-xs text-gray-600">
                        {userProfile ? userProfile.role || 'Member' : 'Not Registered'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Tab Content */}
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WaterUnityDashboard;